import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Share2, Clipboard, Facebook, Twitter, Mail, MessageCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ShareMenuProps {
  title: string;
  url: string;
  text?: string;
  image?: string;
  buttonText?: string; // New prop for button text
}

export function ShareMenu({ title, url, text, image, buttonText }: ShareMenuProps) {
  const { toast } = useToast();
  // Instead of using window.location.origin which can vary, use the fixed domain
  const baseUrl = "https://treguim.org";
  const absoluteUrl = url.startsWith('http') ? url : new URL(url, baseUrl).toString();
  
  // Clean up image URL - remove query parameters that might confuse Facebook
  let absoluteImageUrl = '';
  if (image) {
    // Create a direct URL to our sharing-image endpoint
    absoluteImageUrl = `${baseUrl}/sharing-image?url=${encodeURIComponent(image)}`;
    
    // If no such endpoint exists, use a direct high-quality image
    if (image.startsWith('http')) {
      const imageUrl = new URL(image);
      // Remove any query parameters
      imageUrl.search = '';
      absoluteImageUrl = imageUrl.toString();
    } else {
      const imageUrl = new URL(image, baseUrl);
      imageUrl.search = '';
      absoluteImageUrl = imageUrl.toString();
    }
  } else {
    // Use default sharing image
    absoluteImageUrl = `${baseUrl}/sharing-default.jpg`;
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(absoluteUrl); // Use absoluteUrl for copying
      toast({
        title: "Linku u kopjua",
        description: "Linku u kopjua në clipboard. Mund ta ndani tani.",
      });
    } catch (error) {
      console.error('Failed to copy: ', error);
      toast({
        title: "Gabim",
        description: "Ndodhi një gabim gjatë kopjimit të linkut.",
        variant: "destructive",
      });
    }
  };

  // Generate social media share URLs with simpler URL approach
  const getFacebookShareUrl = () => {
    // Use Facebook's simple sharer.php that relies on og:image tags from the page
    return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(absoluteUrl)}`;
  };

  const getTwitterShareUrl = () => {
    return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text || title)}&url=${encodeURIComponent(absoluteUrl)}`;
  };

  const getEmailShareUrl = () => {
    return `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text || title + '\n\n' + absoluteUrl)}`;
  };
  
  const getWhatsAppShareUrl = () => {
    return `https://wa.me/?text=${encodeURIComponent(text || title + ' ' + absoluteUrl)}`; // Added text prop for WhatsApp
  };

  // Open share URL in a new window
  const openShareWindow = (shareUrl: string) => {
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-full border-gray-300 text-gray-700 dark:text-gray-300">
          <Share2 className="mr-2 h-4 w-4" />
          {buttonText || "Ndaj Produktin"} {/* Use new prop or default text */}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => openShareWindow(getFacebookShareUrl())}>
          <Facebook className="mr-2 h-4 w-4" />
          <span>Facebook</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openShareWindow(getTwitterShareUrl())}>
          <Twitter className="mr-2 h-4 w-4" />
          <span>Twitter</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openShareWindow(getWhatsAppShareUrl())}>
          <MessageCircle className="mr-2 h-4 w-4" />
          <span>WhatsApp</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => window.location.href = getEmailShareUrl()}>
          <Mail className="mr-2 h-4 w-4" />
          <span>Email</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={copyToClipboard}>
          <Clipboard className="mr-2 h-4 w-4" />
          <span>Kopjo linkun</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
